import { useState } from "react";
import { Marca } from "@notes/ui";
import { Onda } from "./Onda.js";

/**
 * Explica por que a entrada pelo CRM não completou. O motivo vem na URL
 * (`?ssoerro=`), porque é um redirecionamento do servidor; o texto é daqui.
 */
export function motivoDoSso(busca: string): string | null {
  const v = new URLSearchParams(busca).get("ssoerro");
  if (v === "sem-acesso") return "Sua conta do CRM ainda não tem acesso a esta agência no Notes. Peça a quem administra o Notes para liberar o seu e-mail.";
  if (v === "invalido") return "O link vindo do CRM venceu ou não é válido. Volte ao CRM e clique de novo em Notes.";
  return null;
}

/**
 * A ENTRADA. Duas colunas: à esquerda a onda, que diz o que o produto faz; à
 * direita o formulário, denso como o resto do sistema. Erro vem PRONTO do
 * servidor e é sempre o mesmo: distinguir "e-mail não existe" de "senha
 * errada" vira oráculo de quem trabalha na agência.
 */
export function Entrar({ aoEntrar, busca = window.location.search }: { aoEntrar: () => void; busca?: string }) {
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState<string | null>(() => motivoDoSso(busca));
  const [enviando, setEnviando] = useState(false);

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      const r = await fetch("/auth/login", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, senha }),
      });
      if (!r.ok) {
        const corpo = await r.json().catch(() => ({})) as { erro?: string };
        setErro(corpo.erro ?? "Não consegui entrar. Confira o e-mail e a senha.");
        return;
      }
      aoEntrar();
    } catch {
      setErro("Não consegui falar com o servidor. Tente de novo.");
    } finally { setEnviando(false); }
  };

  return (
    <div className="enotes enotes-entrada">
      <aside className="enotes-entrada-arte" aria-hidden="true">
        <div className="enotes-entrada-grade" />
        <Onda />
        <div className="enotes-entrada-legenda">
          <p className="enotes-entrada-frase">
            Toda conversa com o cliente diz mais do que cabe na memória,
            <em> e o Notes escuta por você.</em>
          </p>
          <p className="enotes-entrada-sub">
            Reunião, ligação e áudio viram resumo, tarefa, compromisso e
            registro na negociação. O que ficou prometido não se perde.
          </p>
        </div>
      </aside>

      <main className="enotes-entrada-form">
        <div className="enotes-entrada-caixa surge">
          <Marca tamanho={32} />
          <form onSubmit={enviar} className="mt-8">
            <p className="eyebrow">Acesso</p>
            <h1 className="titulo-pagina mt-2">Entrar</h1>
            <p className="mt-1.5 text-sm" style={{ color: "var(--muted-foreground)" }}>
              Use o e-mail da sua agência. Se você já usa o EnturOS CRM, entre por lá com um clique no botão Notes.
            </p>
            <div className="mt-6 space-y-4">
              <label className="block">
                <span className="eyebrow block pb-1.5">E-mail</span>
                <input className="campo" type="email" value={email} autoComplete="username" autoFocus
                  onChange={(ev) => setEmail(ev.target.value)} aria-label="E-mail" required />
              </label>
              <label className="block">
                <span className="eyebrow block pb-1.5">Senha</span>
                <input className="campo" type="password" value={senha} autoComplete="current-password"
                  onChange={(ev) => setSenha(ev.target.value)} aria-label="Senha" required />
              </label>
            </div>
            {erro && (
              <p role="alert" className="enotes-erro">
                <span aria-hidden className="sinal sinal-critico" /><span>{erro}</span>
              </p>
            )}
            <button className="botao mt-6 w-full" data-variante="primario" disabled={enviando}>
              {enviando ? "Entrando" : "Entrar"}
            </button>
            <p className="mt-5 text-xs leading-relaxed" style={{ color: "var(--faint-foreground)" }}>
              O acesso é criado pela sua agência. Sem senha ainda? Fale com quem administra o Notes por lá.
            </p>
          </form>
        </div>
        <p className="enotes-entrada-rodape carimbo">EnturOS · Notes</p>
      </main>
    </div>
  );
}
